import { useId, useRef, useState, type FormEvent } from "react";

import { ApiError, submitInquiry } from "../lib/api";
import { BUDGET_LABELS, TIMELINE_LABELS } from "../lib/site";
import { validateInquiry } from "../lib/validation";
import type { FieldErrors, InquiryPayload } from "../types";
import { AlertIcon, ArrowRightIcon } from "./icons";

const PROJECT_TYPES = [
  "Website",
  "Web App",
  "Mobile App",
  "Product / MVP",
  "Infrastructure & DevOps",
  "Design / Design System",
  "Staff Augmentation",
  "Something Else",
];

const DISCIPLINES = [
  "Frontend",
  "Backend",
  "Fullstack",
  "Infrastructure",
  "Product Design",
  "Project Management",
];

const BUDGETS = ["under-25k", "25k-75k", "75k-150k", "150k-plus", "not-sure"];
const TIMELINES = ["asap", "1-3-months", "3-6-months", "exploring"];

const EMPTY: InquiryPayload = {
  name: "",
  email: "",
  company: "",
  projectType: "",
  disciplines: [],
  budget: "",
  timeline: "",
  message: "",
  website: "",
};

interface ConsultationFormProps {
  onSuccess: (referenceId: string | null) => void;
}

export const ConsultationForm = ({ onSuccess }: ConsultationFormProps) => {
  const [values, setValues] = useState<InquiryPayload>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const fieldId = (name: string) => `${id}-${name}`;
  const errorId = (name: string) => `${id}-${name}-error`;

  const setField = <K extends keyof InquiryPayload>(
    name: K,
    value: InquiryPayload[K],
  ) => {
    setValues((current) => ({ ...current, [name]: value }));
    // Clear the error as soon as the user starts correcting the field.
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const toggleDiscipline = (discipline: string) => {
    setField(
      "disciplines",
      values.disciplines.includes(discipline)
        ? values.disciplines.filter((item) => item !== discipline)
        : [...values.disciplines, discipline],
    );
  };

  const focusFirstError = (fieldErrors: FieldErrors) => {
    const first = Object.keys(fieldErrors)[0];
    if (!first) return;
    // `disciplines` is a checkbox group with no single labelled control.
    const target =
      document.getElementById(fieldId(first)) ??
      document.querySelector<HTMLElement>(`[name="${first}"]`);
    target?.focus();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    const fieldErrors = validateInquiry(values);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      focusFirstError(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitInquiry(values);
      onSuccess(result.id);
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fieldErrors).length > 0) {
        setErrors(error.fieldErrors);
        focusFirstError(error.fieldErrors);
      }
      setFormError(
        error instanceof ApiError
          ? error.message
          : "Something went wrong. Please try again, or email us directly.",
      );
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setIsSubmitting(false);
    }
  };

  const describedBy = (name: keyof InquiryPayload, hintId?: string) => {
    const ids = [errors[name] ? errorId(name) : null, hintId ?? null].filter(
      Boolean,
    );
    return ids.length > 0 ? ids.join(" ") : undefined;
  };

  const renderError = (name: keyof InquiryPayload) =>
    errors[name] ? (
      <p className="field__error" id={errorId(name)}>
        <AlertIcon />
        {errors[name]}
      </p>
    ) : null;

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      {formError ? (
        <div
          className="alert alert--error"
          role="alert"
          tabIndex={-1}
          ref={errorRef}
        >
          <AlertIcon size={18} />
          <span>{formError}</span>
        </div>
      ) : null}

      <div className="form__row">
        <div className="field">
          <label className="field__label" htmlFor={fieldId("name")}>
            Your name
          </label>
          <input
            id={fieldId("name")}
            className="input"
            type="text"
            name="name"
            autoComplete="name"
            value={values.name}
            onChange={(event) => setField("name", event.target.value)}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={describedBy("name")}
            disabled={isSubmitting}
          />
          {renderError("name")}
        </div>

        <div className="field">
          <label className="field__label" htmlFor={fieldId("email")}>
            Work email
          </label>
          <input
            id={fieldId("email")}
            className="input"
            type="email"
            name="email"
            autoComplete="email"
            value={values.email}
            onChange={(event) => setField("email", event.target.value)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={describedBy("email")}
            disabled={isSubmitting}
          />
          {renderError("email")}
        </div>
      </div>

      <div className="form__row">
        <div className="field">
          <label className="field__label" htmlFor={fieldId("company")}>
            Company
            <span className="field__optional">optional</span>
          </label>
          <input
            id={fieldId("company")}
            className="input"
            type="text"
            name="company"
            autoComplete="organization"
            value={values.company}
            onChange={(event) => setField("company", event.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor={fieldId("projectType")}>
            What are you building?
          </label>
          <select
            id={fieldId("projectType")}
            className="select"
            name="projectType"
            value={values.projectType}
            onChange={(event) => setField("projectType", event.target.value)}
            aria-invalid={Boolean(errors.projectType)}
            aria-describedby={describedBy("projectType")}
            disabled={isSubmitting}
          >
            <option value="">Select a project type…</option>
            {PROJECT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {renderError("projectType")}
        </div>
      </div>

      <fieldset
        className="field"
        aria-invalid={Boolean(errors.disciplines)}
        aria-describedby={describedBy("disciplines", `${id}-disciplines-hint`)}
      >
        <legend className="field__label">Which areas do you need?</legend>
        <p className="field__hint" id={`${id}-disciplines-hint`}>
          Select everything that applies — it helps us bring the right people to
          the call.
        </p>
        <div className="checkbox-grid">
          {DISCIPLINES.map((discipline) => (
            <label key={discipline} className="checkbox">
              <input
                type="checkbox"
                name="disciplines"
                value={discipline}
                checked={values.disciplines.includes(discipline)}
                onChange={() => toggleDiscipline(discipline)}
                disabled={isSubmitting}
              />
              {discipline}
            </label>
          ))}
        </div>
        {renderError("disciplines")}
      </fieldset>

      <div className="form__row">
        <div className="field">
          <label className="field__label" htmlFor={fieldId("budget")}>
            Budget range
          </label>
          <select
            id={fieldId("budget")}
            className="select"
            name="budget"
            value={values.budget}
            onChange={(event) => setField("budget", event.target.value)}
            aria-invalid={Boolean(errors.budget)}
            aria-describedby={describedBy("budget")}
            disabled={isSubmitting}
          >
            <option value="">Select a range…</option>
            {BUDGETS.map((budget) => (
              <option key={budget} value={budget}>
                {BUDGET_LABELS[budget] ?? budget}
              </option>
            ))}
          </select>
          {renderError("budget")}
        </div>

        <div className="field">
          <label className="field__label" htmlFor={fieldId("timeline")}>
            Timeline
          </label>
          <select
            id={fieldId("timeline")}
            className="select"
            name="timeline"
            value={values.timeline}
            onChange={(event) => setField("timeline", event.target.value)}
            aria-invalid={Boolean(errors.timeline)}
            aria-describedby={describedBy("timeline")}
            disabled={isSubmitting}
          >
            <option value="">Select a timeline…</option>
            {TIMELINES.map((timeline) => (
              <option key={timeline} value={timeline}>
                {TIMELINE_LABELS[timeline] ?? timeline}
              </option>
            ))}
          </select>
          {renderError("timeline")}
        </div>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={fieldId("message")}>
          Tell us about the project
        </label>
        <p className="field__hint" id={`${id}-message-hint`}>
          What problem are you solving, who is it for, and what does done look
          like? Rough notes are fine.
        </p>
        <textarea
          id={fieldId("message")}
          className="textarea"
          name="message"
          rows={7}
          value={values.message}
          onChange={(event) => setField("message", event.target.value)}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={describedBy("message", `${id}-message-hint`)}
          disabled={isSubmitting}
        />
        {renderError("message")}
      </div>

      <div className="form__honeypot" aria-hidden="true">
        <label htmlFor={fieldId("website")}>Leave this field empty</label>
        <input
          id={fieldId("website")}
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(event) => setField("website", event.target.value)}
        />
      </div>

      <div className="form__footer">
        <p className="form__note">
          We reply within one business day. Your details stay with us.
        </p>
        <button
          type="submit"
          className="btn btn--primary btn--lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner" aria-hidden="true" />
              Sending…
            </>
          ) : (
            <>
              Request consultation
              <ArrowRightIcon className="btn__icon" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};
